import React, { useState, useMemo } from 'react';
import {
    Portal,
    IconButton,
    Tooltip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import { useTranslation } from 'react-i18next';
import AccountCardService from '../../Services/accountcard.service.js';
import AutocompleteSelectFilter from '../Widgets/AutocompleteSelectFilter/AutocompleteSelectFilter.jsx';
import styles from './ReorderByPrefixDialog.module.scss';

const ReorderByPrefixDialog = ({ open, onClose, caseId, token, onSuccess, palCodesOptions = [] }) => {
    const { t, i18n } = useTranslation();
    const dir = i18n.dir();
    const [previewLoading, setPreviewLoading] = useState(false);
    const [commitLoading, setCommitLoading] = useState(false);
    const [allChanges, setAllChanges] = useState([]);
    const [error, setError] = useState(null);
    /** Selected PalCode options (objects with value/label). Empty = show all. */
    const [selectedPalCodes, setSelectedPalCodes] = useState([]);

    const loadPreview = async () => {
        if (!caseId || !token) return;
        setError(null);
        setPreviewLoading(true);
        try {
            const result = await AccountCardService.getReorderByPrefixPreview(caseId, token);
            const list = result?.changes ?? [];
            setAllChanges(Array.isArray(list) ? list : []);
        } catch (err) {
            setError(err?.message || t('ErrorLoadingPreview') || 'Error loading preview');
            setAllChanges([]);
        } finally {
            setPreviewLoading(false);
        }
    };

    const selectedValuesSet = useMemo(
        () => new Set((selectedPalCodes || []).map((o) => (o?.value ?? o?.key ?? '').toString().trim()).filter(Boolean)),
        [selectedPalCodes]
    );

    const filteredChanges = useMemo(() => {
        if (selectedValuesSet.size === 0) return allChanges;
        return allChanges.filter((c) => {
            const code = (c.palCode ?? c.PalCode ?? '').toString().trim();
            return selectedValuesSet.has(code);
        });
    }, [allChanges, selectedValuesSet]);

    const handleCommit = async () => {
        if (!caseId || !token) return;
        if (filteredChanges.length === 0) {
            setError(t('NoChangesToApply') || 'No changes to apply');
            return;
        }
        setError(null);
        setCommitLoading(true);
        try {
            await AccountCardService.reorderByPrefixCommit(caseId, filteredChanges, token);
            if (typeof onSuccess === 'function') onSuccess();
            onClose();
        } catch (err) {
            setError(err?.message || t('ErrorCommittingReorder') || 'Error committing reorder');
        } finally {
            setCommitLoading(false);
        }
    };

    const handleClose = () => {
        setAllChanges([]);
        setSelectedPalCodes([]);
        setError(null);
        onClose();
    };

    const rows = useMemo(
        () =>
            filteredChanges.map((c) => ({
                oldCardId: c.oldCardId ?? c.OldCardId ?? '',
                newCardId: c.newCardId ?? c.NewCardId ?? '',
                name: c.name ?? c.Name ?? '',
                palCode: c.palCode ?? c.PalCode ?? '',
            })),
        [filteredChanges]
    );

    const hasPreview = allChanges.length > 0;
    const canCommit = rows.length > 0;

    if (!open) return null;

    return (
        <Portal>
            <div
                className={`${styles.overlay} ${dir === 'rtl' ? styles.rtl : styles.ltr}`}
                onClick={handleClose}
            >
                <div dir={dir} onClick={(e) => e.stopPropagation()}>
                    <div className={styles.modal}>
                        <div className={styles.body}>
                            <div className={styles.header}>
                                <Tooltip title={t('Close') || 'Close'}>
                                    <IconButton onClick={handleClose} className={styles.closeIcon}>
                                        <CancelIcon className={styles.closeIcon} />
                                    </IconButton>
                                </Tooltip>
                                <div>{t('ReorderByPrefix') || 'Reorder by prefix'}</div>
                                <div />
                            </div>

                            <div className={styles.scroll}>
                                <div className={styles.toolbar}>
                                    <div className={styles.filterField}>
                                        <span className={styles.fieldLabel} dir={dir}>
                                            {t('PalCode') || 'Pal Code'}
                                        </span>
                                        <AutocompleteSelectFilter
                                            options={palCodesOptions || []}
                                            selectedValues={selectedPalCodes}
                                            onChange={(event, newValue) => setSelectedPalCodes(newValue || [])}
                                            label={t('PalCode') || 'Pal Code'}
                                            placeholder={
                                                selectedPalCodes.length === 0
                                                    ? (t('AllPalCodes') || 'All PalCodes')
                                                    : (t('PalCode') || 'Pal Code')
                                            }
                                            isMultiple
                                            direction={dir}
                                            portalMenu
                                            searchable
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        className={styles.ghostBtn}
                                        onClick={loadPreview}
                                        disabled={previewLoading || !caseId || !token}
                                    >
                                        {previewLoading ? (
                                            <CircularProgress className={styles.spinner} size={18} />
                                        ) : null}
                                        {previewLoading ? t('Loading') || 'Loading...' : t('Preview') || 'Preview'}
                                    </button>
                                </div>

                                {error && <p className={styles.error}>{error}</p>}

                                {hasPreview && rows.length === 0 && (
                                    <p className={styles.muted}>
                                        {t('NoChangesForSelectedPalCode') || 'No changes for the selected PalCode.'}
                                    </p>
                                )}

                                {rows.length > 0 && (
                                    <div className={styles.tableWrap}>
                                        <TableContainer component={Paper}>
                                            <Table size="small" stickyHeader>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>
                                                            {t('OldCardId') || 'Old Card ID'}
                                                        </TableCell>
                                                        <TableCell>
                                                            {t('NewCardId') || 'New Card ID'}
                                                        </TableCell>
                                                        <TableCell>
                                                            {t('Name') || 'Name'}
                                                        </TableCell>
                                                        <TableCell>
                                                            {t('PalCode') || 'Pal Code'}
                                                        </TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {rows.map((row, idx) => (
                                                        <TableRow key={idx}>
                                                            <TableCell>{row.oldCardId}</TableCell>
                                                            <TableCell>{row.newCardId}</TableCell>
                                                            <TableCell>{row.name}</TableCell>
                                                            <TableCell>{row.palCode}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </div>
                                )}

                                {!hasPreview && !previewLoading && !error && (
                                    <p className={styles.muted}>
                                        {t('ClickPreviewToSeeChanges') || 'Click Preview to see planned CardId changes.'}
                                    </p>
                                )}
                            </div>

                            <div className={styles.footer}>
                                <button type="button" className={styles.secondaryBtn} onClick={handleClose}>
                                    {t('cancelButton') || 'Cancel'}
                                </button>
                                <button
                                    type="button"
                                    className={styles.primaryBtn}
                                    onClick={handleCommit}
                                    disabled={commitLoading || !canCommit}
                                >
                                    {commitLoading ? (
                                        <CircularProgress className={styles.spinner} size={18} color="inherit" />
                                    ) : null}
                                    {commitLoading ? t('Applying') || 'Applying...' : t('Commit') || 'Commit'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Portal>
    );
};

export default ReorderByPrefixDialog;
